import { ChipPicker } from "./ChipPicker";
import type { ChipOption } from "./ChipPicker";
import { DetailField } from "./DetailField";
import { formatDate } from "../data/format";
import type { MilestoneSummary } from "../data/types";

/** Which band a date falls in, for the picker's optgroups. */
function band(eventDate: string, today: string): string {
  if (eventDate < today) return "Past";
  return "Upcoming";
}

/** A milestone reads as its date and title together — "24 Sep · Certified tier
 *  launch" — because the date is what the chip is for. */
function label(m: MilestoneSummary): string {
  return `${formatDate(m.eventDate)} · ${m.title}`;
}

/**
 * The dates a request has to be ready for.
 *
 * Its own component rather than another block in RequestDetail, which is close
 * to the gate's file-size limit. Cancelled milestones are never offered but
 * still render when already linked, so retiring one cannot strand a request.
 */
export function RequestMilestones({
  milestones,
  candidates,
  disabled,
  onChange,
}: {
  /** The request's current milestones. */
  milestones: MilestoneSummary[];
  /** Every milestone on the request's account — the pool to pick from. */
  candidates: MilestoneSummary[];
  disabled?: boolean;
  onChange: (milestoneIds: string[]) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const options: ChipOption[] = candidates.map((m) => ({
    id: m.id,
    name: label(m),
    group: band(m.eventDate, today),
    selectable: m.status !== "cancelled",
  }));

  return (
    <DetailField label="Milestones">
      <ChipPicker
        id="rd-milestones"
        options={options}
        value={milestones.map((m) => m.id)}
        onChange={onChange}
        disabled={disabled}
        addLabel="Add milestone"
        removeLabel={(name) => `Remove milestone ${name}`}
        placeholder="Link to a date…"
        emptyLabel={candidates.length === 0 ? "No dates on this account yet" : "No more dates to add"}
      />
    </DetailField>
  );
}
